import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, Loader2, Mail, Lock, User, ShieldCheck } from "lucide-react";

const HERO =
  "https://images.pexels.com/photos/8089172/pexels-photo-8089172.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();
  const [state, setState] = useState({ loading: true, valid: false, email: "", permissions: [], error: "" });
  const [form, setForm] = useState({ name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invite/${token}`);
        setState({ loading: false, valid: true, email: data.email, permissions: data.permissions || [], error: "" });
      } catch (e) {
        setState({ loading: false, valid: false, email: "", permissions: [], error: formatApiErrorDetail(e.response?.data?.detail) || "Invalid invitation" });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await acceptInvite(token, { name: form.name, password: form.password });
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-[calc(100vh-73px)] lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#2C3830]/40" />
      </div>
      <div className="flex items-center justify-center px-5 py-12 lg:px-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#2C3830] text-white"><Building2 className="h-5 w-5" /></span>
            <span className="font-display text-2xl font-semibold text-[#2C3830]">RENTO</span>
          </div>

          {state.loading ? (
            <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#2C3830]" /></div>
          ) : !state.valid ? (
            <div data-testid="invite-invalid" className="mt-10">
              <h1 className="font-display text-2xl font-medium text-[#1A1A1A]">Invitation unavailable</h1>
              <p className="mt-2 text-sm text-[#6B7280]">{state.error}</p>
              <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#2C3830] text-white">Go home</Button>
            </div>
          ) : (
            <>
              <div className="mt-8 flex items-center gap-2 text-[#2C3830]">
                <ShieldCheck className="h-5 w-5" />
                <h1 className="font-display text-2xl font-medium tracking-tight text-[#1A1A1A]">Join the admin team</h1>
              </div>
              <p className="mt-2 text-sm text-[#6B7280]">
                Set up your RENTO admin account. Access: <strong>{state.permissions.map((p) => p.replace("_", " ")).join(", ")}</strong>.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
                    <Input data-testid="invite-email" value={state.email} disabled className="rounded-xl pl-9 bg-[#F3F4F6]" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Full name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
                    <Input data-testid="invite-name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Jane Doe" required className="rounded-xl pl-9" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Create a password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
                    <Input data-testid="invite-password" type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} placeholder="At least 6 characters" required minLength={6} className="rounded-xl pl-9" />
                  </div>
                </div>
                {error && <p data-testid="invite-error" className="text-sm text-destructive">{error}</p>}
                <Button data-testid="invite-submit" type="submit" disabled={submitting} className="w-full rounded-full bg-[#2C3830] py-6 text-white hover:bg-[#3A4A40]">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Accept invitation"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
